import { useState, useEffect } from "react";

const API_BASE = `${process.env.REACT_APP_API_URL || "https://api.mioprice.com"}/api/healthcare`;

// ── Location helpers ─────────────────────────────────────────────────────────
const zipCityCache2 = {};
const getZipCityCC = async (zip) => {
  if (zipCityCache2[zip]) return zipCityCache2[zip];
  try {
    const r = await fetch(`https://api.zippopotam.us/us/${zip}`);
    if (r.ok) { const d = await r.json(); const city = d.places?.[0]?.["place name"] || zip; const state = d.places?.[0]?.["state abbreviation"] || ""; const label = city + (state ? ", " + state : ""); zipCityCache2[zip] = label; return label; }
  } catch {}
  return zip;
};
const cityCoordCacheCC = {};
const geocodeCityCC = async (input) => {
  const key = input.toLowerCase().trim();
  if (cityCoordCacheCC[key]) return cityCoordCacheCC[key];
  try {
    const q = encodeURIComponent(key + (key.includes(",") ? "" : ", USA"));
    const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=5&q=${q}&countrycodes=us&addressdetails=1`, { headers: { "User-Agent": "MioPrice/1.0" } });
    if (r.ok) { const d = await r.json(); if (d.length > 0) { const best = d.find(x => ["city","town"].includes(x.type)) || d[0]; const city = best.address?.city || best.address?.town || (best.display_name||"").split(",")[0]; const state = best.address?.state_code || ""; const label = (city||"").trim() + (state ? ", " + state : ""); const result = { lat: parseFloat(best.lat), lng: parseFloat(best.lon), label }; cityCoordCacheCC[key] = result; return result; } }
  } catch {}
  return null;
};
const isZipCodeCC = (s) => /^\d{5}$/.test((s||"").trim());


// ── Alert thresholds — tune without touching logic ────────────────────────────
const BETTER_OPTION_MIN_SAVINGS     = 50;   // $ min savings to show better option alert
const BETTER_OPTION_MIN_SAVINGS_PCT = 0.20; // % min savings to show better option alert
const BETTER_OPTION_RADIUS_MULT     = 2;    // wider radius multiplier
const BETTER_OPTION_RADIUS_MAX      = 50;   // cap in miles

const PAYERS = [
  { id: "uhc",   label: "UnitedHealthcare (UHC)"  },
  { id: "aetna", label: "Aetna"                   },
  { id: "cigna", label: "Cigna"                   },
];

const PROCEDURES = [
  "Knee MRI", "Hip replacement", "Knee replacement", "Colonoscopy",
  "Blood work", "Mammogram", "CT scan", "Chest X-ray",
  "EKG", "Echocardiogram", "Stress test", "Brain MRI",
  "Spine MRI", "Shoulder MRI", "Ultrasound", "Bone density",
  "Cataract surgery", "ACL repair", "Rotator cuff", "Carpal tunnel",
  "Gallbladder removal", "Hernia repair", "Annual physical",
  "Pap smear", "Pelvic ultrasound", "Therapy", "Sleep study",
  "Epidural", "Spinal fusion", "Endoscopy", "Emergency room",
];

const fmt = (n) => "$" + Math.round(n).toLocaleString();

const ACRONYMS_CC = new Set(["ssm","osf","uic","ui","rml","amita","bmc","umc","inc","llc","er","icu","nicu","picu","va","asc","ii","iii","iv","pc","sc","usa"]);
const SMALL_CC    = new Set(["of","and","the","at","for","in","on","to","a","an"]);
const capWordCC   = (w) => { const wl = w.toLowerCase(); if (!wl) return w; if (wl.startsWith("mc") && wl.length > 2) return "Mc" + wl[2].toUpperCase() + wl.slice(3); if (wl.startsWith("mac") && wl.length > 4) return "Mac" + wl[3].toUpperCase() + wl.slice(4); return wl[0].toUpperCase() + wl.slice(1); };
const titleCaseCC = (name) => { if (!name) return name; const tokens = name.trim().split(" "); const last = tokens.length - 1; return tokens.map((tok, i) => { const low = tok.toLowerCase(); const core = low.replace(/[^a-z]/g, ""); if (ACRONYMS_CC.has(core)) return tok.toUpperCase(); if (SMALL_CC.has(low) && i > 0 && i < last) return low; return tok.split(/([-/])/).map(p => (p === "-" || p === "/" ? p : capWordCC(p))).join(""); }).join(" "); };

function calcOOP(negotiatedRate, deductibleTotal, deductibleMet, coinsurance, oopMax) {
  const remaining = Math.max(0, deductibleTotal - deductibleMet);
  if (negotiatedRate <= remaining) {
    return Math.min(negotiatedRate, oopMax);
  }
  const afterDeductible = negotiatedRate - remaining;
  const coinsurancePart = afterDeductible * (coinsurance / 100);
  return Math.min(remaining + coinsurancePart, oopMax);
}

export default function CostCheck() {
  const [payer,          setPayer]          = useState("uhc");
  const [procedure,      setProcedure]      = useState("Knee MRI");
  const [zip,            setZip]            = useState("");
  const [zipLoading,     setZipLoading]     = useState(false);
  const [zipSource,      setZipSource]      = useState("");
  const [cityListCC,     setCityListCC]     = useState([]);
  const [searchTermsCC,  setSearchTermsCC]  = useState([]);
  const [procSugs,       setProcSugs]       = useState([]);
  const [showProcSug,    setShowProcSug]    = useState(false);
  const [citySugsCC,     setCitySugsCC]     = useState([]);
  const [showCitySugCC,  setShowCitySugCC]  = useState(false);
  const [locationLabelCC,setLocationLabelCC]= useState("");

  // Load city list + search terms for autocomplete
  useEffect(() => {
    const API = process.env.REACT_APP_API_URL || "https://api.mioprice.com";
    fetch(API + "/api/cities").then(r => r.json()).then(d => { if (d.cities) setCityListCC(d.cities); }).catch(() => {});
    fetch(API + "/api/search-terms").then(r => r.json()).then(d => {
      if (d.terms) { const tc = s => s.replace(/\b\w/g, c => c.toUpperCase()); setSearchTermsCC([...new Set(d.terms.map(t => tc(t.term)))].sort()); }
    }).catch(() => {});
  }, []);

  // Auto-detect ZIP from browser geolocation on mount (mirrors EnrollmentAdvisor)
  useEffect(() => {
    if (!navigator.geolocation) return;
    setZipLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`,
            { headers: { "Accept-Language": "en-US" } }
          );
          const data = await res.json();
          const detectedZip = data?.address?.postcode?.slice(0, 5);
          if (detectedZip && /^\d{5}$/.test(detectedZip)) {
            setZip(detectedZip);
            setZipSource("gps");
          } else {
            setZipSource("error");
          }
        } catch {
          setZipSource("error");
        }
        setZipLoading(false);
      },
      () => { setZipLoading(false); setZipSource("error"); },
      { timeout: 8000, maximumAge: 300000 }
    );
  }, []);
  const [deductibleTotal,setDeductibleTotal]= useState("");
  const [deductibleMet,  setDeductibleMet]  = useState("");
  const [coinsurance,    setCoinsurance]    = useState(20);
  const [oopMax,         setOopMax]         = useState("");
  const [radius,         setRadius]         = useState(15);
  const [results,        setResults]        = useState([]);
  const [selCat,         setSelCat]          = useState('Facility');
  const [breakdownCard,  setBreakdownCard]   = useState(null);
  const [running,        setRunning]        = useState(false);
  const [error,          setError]          = useState(null);
    const [meta,           setMeta]           = useState(null);
  const [advice,         setAdvice]         = useState(null);
  const [adviceLoading,  setAdviceLoading]  = useState(false);
  const [betterOption,   setBetterOption]   = useState(null);
  const [toastVisible,   setToastVisible]   = useState(false);

  const getAdvice = async (facilityResults) => {
    if (!facilityResults.length) return;
    setAdviceLoading(true); setAdvice(null);
    const top3 = facilityResults.slice(0, 3).map((r, i) =>
      `${i+1}. ${r.facility} (${r.address || r.city}) — you pay $${Math.round(r.oop)}, negotiated rate $${Math.round(r.minRate)}`
    ).join("\n");
    const remaining = Math.max(0, deductibleTotal - deductibleMet);
    const payerName = PAYERS.find(p => p.id === payer)?.label || payer;
    const savings12 = facilityResults.length > 1 ? Math.round(facilityResults[1].oop - facilityResults[0].oop) : 0;
    const prompt = `A patient is comparing prices for "${procedure}" near ZIP ${zip} under ${payerName}.\n\nPlan details:\n- Annual deductible: $${deductibleTotal}\n- Already paid: $${deductibleMet} (${remaining > 0 ? "$"+Math.round(remaining)+" remaining" : "fully met"})\n- Coinsurance: ${coinsurance}%\n- Out-of-pocket max: $${oopMax}\n\nTop 3 facilities by out-of-pocket cost (sorted cheapest first):\n${top3}\n\nIn 3-4 plain English sentences explain:\n1. Why the top facility is cheapest given their deductible status\n2. The dollar savings vs the next option (savings12 = $${savings12})\n3. If a slightly further facility offers significant savings, mention it (e.g. "traveling X more miles saves you $Y")\n4. One practical tip: confirm in-network status or ask about separate facility fees\nStay strictly on cost and pricing. End with a reminder to confirm with their insurer before scheduling.`;
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL || "https://api.mioprice.com"}/api/advisor`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt })
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setAdvice(data.advice || "Unable to load advisor.");
    } catch {
      setAdvice("Unable to load advisor. Please try again.");
    } finally {
      setAdviceLoading(false);
    }
  };

  const runCalc = async () => {
    if (!zip.trim() || running) return;
    setRunning(true); setResults([]); setError(null); setMeta(null); setAdvice(null); setBetterOption(null); setToastVisible(false); setSelCat('Facility');
    try {
      let params;
      const z = zip.trim();
      if (isZipCodeCC(z)) {
        params = new URLSearchParams({ query: procedure, zip: z, radius, limit: 500, payer });
      } else {
        const geo = await geocodeCityCC(z);
        if (!geo) { alert(`Could not find "${z}". Try a zip code.`); setRunning(false); return; }
        setLocationLabelCC(geo.label);
        params = new URLSearchParams({ query: procedure, lat: geo.lat, lng: geo.lng, loc: geo.label, radius, limit: 500, payer });
      }
      const res  = await fetch(API_BASE + "?" + params.toString());
      const data = await res.json();
      if (data.error) { setError(data.error); return; }

      const enriched = (data.results || [])
        .filter(r => r.rates?.length > 0)
        .map(r => {
          // Use professional rates first; fall back to institutional
          const profRates = r.rates.filter(rt => rt.payer === payer && rt.billing_class === "professional");
          const instRates = r.rates.filter(rt => rt.payer === payer && rt.billing_class === "institutional");
          const payerRates = profRates.length ? profRates : instRates;
          const billingClass = profRates.length ? "professional" : "institutional";
          const minRate = payerRates.length ? Math.min(...payerRates.map(rt => rt.min_rate)) : null;
          const maxRate = payerRates.length ? Math.max(...payerRates.map(rt => rt.max_rate)) : null;
          if (!minRate) return null;
          const oop    = calcOOP(minRate, deductibleTotal, deductibleMet, coinsurance, oopMax);
          const oopMax2 = calcOOP(maxRate, deductibleTotal, deductibleMet, coinsurance, oopMax);
          return { ...r, minRate, maxRate, billingClass, oop, oopMax: oopMax2 };
        })
        .filter(Boolean)
        .sort((a, b) => a.oop - b.oop);

      setResults(enriched);
      setMeta({ description: data.description, count: enriched.length });

      // Background search — wider radius to find better options nearby
      if (enriched.length > 0) {
        const bestOOP = enriched[0].oop;
        const widerRadius = Math.min(radius * BETTER_OPTION_RADIUS_MULT, BETTER_OPTION_RADIUS_MAX);
        if (widerRadius > radius) {
          fetch(API_BASE + "?" + new URLSearchParams(isZipCodeCC(zip.trim()) ? { query: procedure, zip: zip.trim(), radius: widerRadius, limit: 500, payer } : { query: procedure, lat: locationLabelCC.lat||zip, lng: locationLabelCC.lng||zip, radius: widerRadius, limit: 500, payer }).toString())
            .then(r => r.json())
            .then(wideData => {
              const wideEnriched = (wideData.results || [])
                .filter(r => r.rates?.length > 0)
                .map(r => {
                  const profRates = r.rates.filter(rt => rt.payer === payer && rt.billing_class === "professional");
                  const instRates = r.rates.filter(rt => rt.payer === payer && rt.billing_class === "institutional");
                  const payerRates = profRates.length ? profRates : instRates;
                  const billingClass = profRates.length ? "professional" : "institutional";
                  const minRate = payerRates.length ? Math.min(...payerRates.map(rt => rt.min_rate)) : null;
                  const maxRate = payerRates.length ? Math.max(...payerRates.map(rt => rt.max_rate)) : null;
                  if (!minRate) return null;
                  const oop    = calcOOP(minRate, deductibleTotal, deductibleMet, coinsurance, oopMax);
                  const oopMax2 = calcOOP(maxRate, deductibleTotal, deductibleMet, coinsurance, oopMax);
                  return { ...r, minRate, maxRate, billingClass, oop, oopMax: oopMax2 };
                })
                .filter(Boolean)
                .sort((a, b) => a.oop - b.oop);

              // Only alert if meaningfully cheaper — 20%+ savings or at least $50
              if (wideEnriched.length > 0) {
                const wideBest = wideEnriched[0];
                const savings = bestOOP - wideBest.oop;
                const savingsPct = savings / bestOOP;
                // Make sure it's not already in current results
                const alreadyShown = enriched.some(r => r.npi === wideBest.npi);
                if (!alreadyShown && savings >= BETTER_OPTION_MIN_SAVINGS && savingsPct >= BETTER_OPTION_MIN_SAVINGS_PCT) {
                  setBetterOption({ facility: wideBest.facility, oop: wideBest.oop, savings, address: wideBest.address, widerRadius });
                  setToastVisible(true);
                  setTimeout(() => setToastVisible(false), 6000);
                }
              }
            })
            .catch(() => {}); // Silent fail — this is background only
        }
      }
    } catch {
      setError("Failed to fetch results. Please try again.");
    } finally {
      setRunning(false);
    }
  };

  const remaining = Math.max(0, deductibleTotal - deductibleMet);
  const payerLabel = PAYERS.find(p => p.id === payer)?.label || payer;

  return (
    <div style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", color: "#111827" }}>

      {/* Input panel */}
      <div style={{ background: "#fff", borderBottom: "1px solid #e5e7eb", padding: "16px" }}>
        <div style={{ maxWidth: 860, margin: "0 auto" }}>
          <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 14 }}>
            Enter your plan details to see your actual out-of-pocket cost — not the sticker price, not an estimate. The real negotiated rate adjusted for your deductible.
          </div>

          {/* Row 1: Payer + Procedure + ZIP */}
          <div style={{ display: "flex", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
            <div style={{ flex: "0 0 160px" }}>
              <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4 }}>Insurance plan</div>
              <select value={payer} onChange={e => setPayer(e.target.value)} style={{ width: "100%", border: "1.5px solid #e5e7eb", borderRadius: 8, padding: "9px 10px", fontSize: 13, background: "#fff", color: "#111827" }}>
                {PAYERS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
            </div>
            <div style={{ flex: 2, minWidth: 180 }}>
              <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4 }}>Procedure</div>
              <div style={{ position: "relative" }}>
                <input value={procedure}
                  onChange={e => { const v = e.target.value; setProcedure(v); if (v.length >= 2) { const m = searchTermsCC.filter(s => s.toLowerCase().includes(v.toLowerCase())).slice(0,8); setProcSugs(m); setShowProcSug(m.length > 0); } else { setProcSugs([]); setShowProcSug(false); } }}
                  onBlur={() => setTimeout(() => setShowProcSug(false), 150)}
                  placeholder="Search procedure…"
                  style={{ width: "100%", border: "1.5px solid #e5e7eb", borderRadius: 8, padding: "9px 10px", fontSize: 13, background: "#fff", color: "#111827", boxSizing: "border-box" }} />
                {showProcSug && procSugs.length > 0 && (
                  <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.1)", zIndex: 100, overflow: "hidden", maxHeight: 220, overflowY: "auto" }}>
                    {procSugs.map((s, i) => (
                      <div key={s} onMouseDown={() => { setProcedure(s); setProcSugs([]); setShowProcSug(false); }}
                        style={{ padding: "9px 14px", cursor: "pointer", fontSize: 13, borderBottom: i < procSugs.length-1 ? "1px solid #f3f4f6" : "none" }}>
                        🔍 {s}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div style={{ flex: 1, minWidth: 150 }}>
              <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4, display: "flex", alignItems: "center", gap: 5 }}>ZIP code
                {zipLoading && <span style={{ fontSize: 9, color: "#d97706" }}>📍 Detecting…</span>}
                {zipSource === "gps" && !zipLoading && <span style={{ fontSize: 9, color: "#16a34a", fontWeight: 600 }}>📍 Auto</span>}
              </div>
              <div style={{ position: "relative" }}>
              <input value={zip} onBlur={() => setTimeout(() => setShowCitySugCC(false), 150)} onChange={e => { const v = e.target.value; setZip(v); setZipSource("manual"); setLocationLabelCC(""); if (v.length >= 2 && !isZipCodeCC(v)) { const m = cityListCC.filter(c => c.toLowerCase().startsWith(v.toLowerCase())).slice(0,6); setCitySugsCC(m); setShowCitySugCC(m.length > 0); } else { setCitySugsCC([]); setShowCitySugCC(false); } }} placeholder={zipLoading ? "Detecting…" : "ZIP or city"} style={{ width: "100%", border: "1.5px solid #e5e7eb", borderRadius: 8, padding: "9px 10px", fontSize: 13, boxSizing: "border-box" }} />
              {showCitySugCC && citySugsCC.length > 0 && (
                <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.1)", zIndex: 100, overflow: "hidden" }}>
                  {citySugsCC.map((city, i) => (
                    <div key={city} onMouseDown={() => { setZip(city); setLocationLabelCC(city); setCitySugsCC([]); setShowCitySugCC(false); }}
                      style={{ padding: "9px 14px", cursor: "pointer", fontSize: 13, borderBottom: i < citySugsCC.length-1 ? "1px solid #f3f4f6" : "none" }}>
                      📍 {city}
                    </div>
                  ))}
                </div>
              )}
              </div>
            </div>
            <div style={{ width: 80 }}>
              <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4 }}>Radius</div>
              <select value={radius} onChange={e => setRadius(Number(e.target.value))} style={{ width: "100%", border: "1.5px solid #e5e7eb", borderRadius: 8, padding: "9px 8px", fontSize: 13, background: "#fff", color: "#111827" }}>
                {[5,10,15,20,30,50].map(r => <option key={r} value={r}>{r} mi</option>)}
              </select>
            </div>
          </div>

          {/* Row 2: Plan details */}
          <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 130 }}>
              <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4 }}>Annual deductible</div>
              <input type="number" value={deductibleTotal || ""} onChange={e => setDeductibleTotal(Number(e.target.value))} onFocus={e => e.target.select()} placeholder="e.g. 1500" style={{ width: "100%", border: "1.5px solid #e5e7eb", borderRadius: 8, padding: "9px 10px", fontSize: 13, boxSizing: "border-box" }} />
            </div>
            <div style={{ flex: 1, minWidth: 130 }}>
              <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4 }}>Already paid toward deductible</div>
              <input type="number" value={deductibleMet || ""} onChange={e => setDeductibleMet(Number(e.target.value))} onFocus={e => e.target.select()} placeholder="e.g. 500" style={{ width: "100%", border: "1.5px solid #e5e7eb", borderRadius: 8, padding: "9px 10px", fontSize: 13, boxSizing: "border-box" }} />
            </div>
            <div style={{ flex: 1, minWidth: 110 }}>
              <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4 }}>Coinsurance after deductible</div>
              <div style={{ position: "relative" }}>
                <input type="number" value={coinsurance || ""} onChange={e => setCoinsurance(Number(e.target.value))} onFocus={e => e.target.select()} placeholder="e.g. 20" style={{ width: "100%", border: "1.5px solid #e5e7eb", borderRadius: 8, padding: "9px 32px 9px 10px", fontSize: 13, boxSizing: "border-box" }} />
                <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", fontSize: 13, color: "#6b7280" }}>%</span>
              </div>
            </div>
            <div style={{ flex: 1, minWidth: 110 }}>
              <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4 }}>Out-of-pocket max</div>
              <input type="number" value={oopMax || ""} onChange={e => setOopMax(Number(e.target.value))} onFocus={e => e.target.select()} placeholder="e.g. 5000" style={{ width: "100%", border: "1.5px solid #e5e7eb", borderRadius: 8, padding: "9px 10px", fontSize: 13, boxSizing: "border-box" }} />
            </div>
            <div style={{ display: "flex", alignItems: "flex-end" }}>
              <button onClick={runCalc} disabled={running || !zip.trim()} style={{ background: running || !zip.trim() ? "#93c5fd" : "#2563eb", border: "none", borderRadius: 8, padding: "9px 20px", color: "#fff", fontSize: 13, fontWeight: 600, cursor: running || !zip.trim() ? "not-allowed" : "pointer", whiteSpace: "nowrap" }}>
                {running ? "Calculating…" : "Show My Cost"}
              </button>
            </div>
          </div>

          {/* ── Deductible & OOP status — visual progress ── */}
          {(() => {
            const dedPct    = deductibleTotal > 0 ? Math.min((deductibleMet / deductibleTotal) * 100, 100) : 0;
            const oopSpent  = Math.min(deductibleMet, oopMax);
            const oopPct    = oopMax > 0 ? Math.min((oopSpent / oopMax) * 100, 100) : 0;
            const dedMet    = remaining === 0;
            const oopInvalid = oopMax > 0 && deductibleTotal > 0 && oopMax < deductibleTotal;
            const oopMet    = !oopInvalid && oopSpent >= oopMax && oopMax > 0;

            return (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 4 }}>

                {/* Deductible progress */}
                <div style={{ background: dedMet ? "#f0fdf4" : "#fffbeb", border: `1px solid ${dedMet ? "#bbf7d0" : "#fde68a"}`, borderRadius: 10, padding: "10px 14px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: "#6b7280" }}>
                      DEDUCTIBLE {dedMet ? "✅ MET" : "PROGRESS"}
                    </span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: dedMet ? "#16a34a" : "#92400e" }}>
                      {fmt(deductibleMet)} / {fmt(deductibleTotal)}
                    </span>
                  </div>
                  {/* Progress bar */}
                  <div style={{ height: 8, background: "#e5e7eb", borderRadius: 6, overflow: "hidden", marginBottom: 6 }}>
                    <div style={{ width: `${dedPct}%`, height: "100%", background: dedMet ? "#16a34a" : "#f59e0b", borderRadius: 6, transition: "width 0.4s" }} />
                  </div>
                  <div style={{ fontSize: 11, color: dedMet ? "#16a34a" : "#92400e" }}>
                    {dedMet
                      ? `✓ Deductible met — paying ${coinsurance}% coinsurance`
                      : `${fmt(remaining)} remaining — you pay 100% until met`}
                  </div>
                </div>

                {/* OOP Max progress */}
                <div style={{ background: oopInvalid ? "#fffbeb" : oopMet ? "#f0fdf4" : "#f8fafc", border: `1px solid ${oopInvalid ? "#fde68a" : oopMet ? "#bbf7d0" : "#e5e7eb"}`, borderRadius: 10, padding: "10px 14px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: "#6b7280" }}>
                      OOP MAX {oopInvalid ? "⚠️ CHECK ENTRY" : oopMet ? "✅ REACHED" : "PROGRESS"}
                    </span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: oopInvalid ? "#92400e" : oopMet ? "#16a34a" : "#374151" }}>
                      {fmt(oopSpent)} / {fmt(oopMax)}
                    </span>
                  </div>
                  {/* Progress bar */}
                  <div style={{ height: 8, background: "#e5e7eb", borderRadius: 6, overflow: "hidden", marginBottom: 6 }}>
                    <div style={{ width: `${oopPct}%`, height: "100%", background: oopInvalid ? "#f59e0b" : oopMet ? "#16a34a" : "#2563eb", borderRadius: 6, transition: "width 0.4s" }} />
                  </div>
                  <div style={{ fontSize: 11, color: oopMet ? "#16a34a" : "#6b7280" }}>
                    {oopMet
                      ? "✓ Insurance covers 100% of in-network costs"
                      : `${fmt(oopMax - oopSpent)} more until insurance covers 100%`}
                  </div>
                </div>

              </div>
            );
          })()}
        </div>
      </div>

      {/* Results */}
      <div style={{ maxWidth: 860, margin: "0 auto", padding: "16px" }}>
        {error && (
          <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: "14px", color: "#dc2626", fontSize: 13, marginBottom: 16 }}>⚠️ {error}</div>
        )}

        {results.length > 0 && (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
              <div style={{ fontSize: 13, color: "#6b7280" }}>
                <strong style={{ color: "#111827" }}>{meta?.description}</strong> · {payerLabel} · {radius}mi from {zip}
                <span style={{ marginLeft: 8, fontWeight: 600, color: "#16a34a" }}>{results.length} facilities found</span>
              </div>
              {/* Cost model pill */}
              <div style={{ fontSize: 11, background: remaining > 0 ? "#fffbeb" : "#f0fdf4", border: `1px solid ${remaining > 0 ? "#fde68a" : "#bbf7d0"}`, borderRadius: 20, padding: "4px 12px", color: remaining > 0 ? "#92400e" : "#15803d", fontWeight: 600, whiteSpace: "nowrap" }}>
                {remaining > 0
                  ? `⚠️ ${fmt(remaining)} deductible remaining`
                  : `✅ Deductible met · paying ${coinsurance}% coinsurance`}
              </div>
            </div>

            {/* Toast — fixed top-right, slides in, auto-dismisses in 6s */}
            {betterOption && toastVisible && (
              <div style={{ position: "fixed", top: 80, right: 20, zIndex: 1000, width: 320, background: "linear-gradient(135deg, #fffbeb, #fef3c7)", border: "1.5px solid #f59e0b", borderRadius: 14, padding: "12px 14px", boxShadow: "0 8px 24px rgba(0,0,0,0.15)", display: "flex", alignItems: "flex-start", gap: 10, animation: "slideIn 0.3s ease" }}>
                <style>{`@keyframes slideIn{from{transform:translateX(120%);opacity:0}to{transform:translateX(0);opacity:1}}`}</style>
                <span style={{ fontSize: 18, flexShrink: 0, marginTop: 1 }}>💡</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#92400e", marginBottom: 3 }}>Better price found nearby</div>
                  <div style={{ fontSize: 12, color: "#78350f", lineHeight: 1.5 }}>
                    <strong>{betterOption.facility}</strong> — you'd pay <strong style={{ color: "#16a34a" }}>${Math.round(betterOption.oop)}</strong> · saves <strong>${Math.round(betterOption.savings)}</strong>
                  </div>
                  <button onClick={() => { setToastVisible(false); setRadius(betterOption.widerRadius); setTimeout(() => runCalc(), 100); }} style={{ marginTop: 8, background: "#f59e0b", border: "none", borderRadius: 7, padding: "5px 12px", fontSize: 11, fontWeight: 600, color: "#fff", cursor: "pointer" }}>
                    Expand to {betterOption.widerRadius}mi →
                  </button>
                </div>
                <button onClick={() => setToastVisible(false)} style={{ background: "none", border: "none", fontSize: 16, color: "#92400e", cursor: "pointer", flexShrink: 0, padding: 0 }}>×</button>
              </div>
            )}

            {/* AI Advisor — on demand */}
            {!advice && !adviceLoading && (
              <button onClick={() => getAdvice(results)} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16, padding: "12px 16px", background: "linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)", border: "1.5px solid #d8b4fe", borderRadius: 12, cursor: "pointer", width: "100%", boxSizing: "border-box" }}>
                <div style={{ width: 32, height: 32, borderRadius: "50%", background: "linear-gradient(135deg, #9333ea, #7c3aed)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, flexShrink: 0 }}>✨</div>
                <div style={{ textAlign: "left", flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#7c3aed" }}>Get personalized advice on these options</div>
                  <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>AI explains your best option based on your plan · Cost analysis only</div>
                </div>
                <span style={{ fontSize: 18, color: "#9333ea" }}>→</span>
              </button>
            )}
            {adviceLoading && (
              <div style={{ marginBottom: 16, padding: "14px 16px", background: "#f0f7ff", border: "1px solid #bfdbfe", borderRadius: 10, display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 16, height: 16, border: "2px solid #bfdbfe", borderTopColor: "#2563eb", borderRadius: "50%", animation: "spin 0.8s linear infinite", flexShrink: 0 }} />
                <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
                <span style={{ fontSize: 13, color: "#6b7280" }}>Analyzing your plan and options...</span>
              </div>
            )}
            {advice && (
              <div style={{ marginBottom: 16, padding: "16px", background: "linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)", border: "1.5px solid #d8b4fe", borderRadius: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                  <div style={{ width: 28, height: 28, borderRadius: "50%", background: "linear-gradient(135deg, #9333ea, #7c3aed)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, flexShrink: 0, color: "#fff" }}>✨</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#7c3aed" }}>MioPrice Advisor</div>
                  <button onClick={() => setAdvice(null)} style={{ marginLeft: "auto", background: "none", border: "none", fontSize: 16, color: "#9ca3af", cursor: "pointer" }}>×</button>
                </div>
                <div style={{ fontSize: 13, color: "#4c1d95", lineHeight: 1.8, whiteSpace: "pre-wrap" }}>{advice}</div>
              </div>
            )}

            {/* ── Category filter chips ── */}
            {results.length > 0 && (() => {
              const CATS = [
                { key: 'Facility',            label: 'Hospitals & Facilities' },
                { key: 'Individual Providers',label: 'Doctors & Clinicians' },
                { key: 'Ancillary',           label: 'Labs & Medical Services' },
                { key: 'Behavioral Health',   label: 'Behavioral Health' },
                { key: 'all',                label: 'All' },
              ];
              return (
                <div style={{ display: 'flex', gap: 6, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', gap: 4, background: '#f3f4f6', borderRadius: 8, padding: 3, overflowX: 'auto', maxWidth: '100%', WebkitOverflowScrolling: 'touch' }}>
                  {CATS.map(cat => {
                    const cnt = cat.key === 'all' ? results.length
                      : results.filter(r => r.provider_category === cat.key).length;
                    if (cnt === 0 && cat.key !== 'all') return null;
                    const active = selCat === cat.key;
                    return (
                      <button key={cat.key} onClick={() => setSelCat(cat.key)}
                        style={{ fontSize: 12, padding: '5px 12px', borderRadius: 6, border: 'none',
                          background: active ? '#fff' : 'transparent',
                          color: active ? '#2563eb' : '#6b7280',
                          cursor: 'pointer', fontWeight: active ? 600 : 400,
                          boxShadow: active ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                          whiteSpace: 'nowrap', flexShrink: 0, fontFamily: 'inherit' }}>
                        {cat.label} ({cnt})
                      </button>
                    );
                  })}
                  </div>
                </div>
              );
            })()}

            {(selCat === 'all' ? results : results.filter(r => r.provider_category === selCat)).map((r, i) => {
              const isTop = i === 0;
              const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(r.address || r.facility)}`;
              const rateDisplay = r.minRate === r.maxRate ? fmt(r.minRate) : `${fmt(r.minRate)}–${fmt(r.maxRate)}`;
              const oopExplain = r.minRate <= remaining
                ? `Deductible not yet met — you pay 100% of the ${rateDisplay} negotiated rate`
                : remaining > 0
                  ? `You pay ${fmt(remaining)} remaining deductible + ${coinsurance}% of ${fmt(r.minRate - remaining)}`
                  : `Deductible met — you pay ${coinsurance}% of the ${rateDisplay} negotiated rate`;

              return (
                <div key={r.npi} style={{ background: "#fff", borderRadius: 14, marginBottom: 10, border: `1.5px solid ${isTop ? "#bfdbfe" : "#e5e7eb"}`, boxShadow: isTop ? "0 2px 12px rgba(37,99,235,0.08)" : "0 1px 3px rgba(0,0,0,0.04)", overflow: "hidden" }}>
                  {isTop && (
                    <div style={{ background: "#eff6ff", borderBottom: "1px solid #bfdbfe", padding: "5px 16px", display: "flex", alignItems: "center", gap: 6 }}>
                      <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#2563eb" }} />
                      <span style={{ fontSize: 11, fontWeight: 600, color: "#1d4ed8" }}>Lowest out-of-pocket cost for this procedure</span>
                    </div>
                  )}
                  <div style={{ padding: "14px 16px" }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "flex-start", gap: 6, marginBottom: 3 }}>
                          <span style={{ fontSize: 16, lineHeight: 1.4, flexShrink: 0 }}>{r.type === "Medical Facility" ? "🏥" : "👨‍⚕️"}</span>
                          <span style={{ fontSize: 14, fontWeight: 700, color: "#111827", lineHeight: 1.4 }}>{titleCaseCC(r.facility)}</span>
                        </div>
                        <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4, display: "flex", gap: 6, alignItems: "center" }}>
                          <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0, flex: 1 }}>📍 {titleCaseCC(r.address)}</span>
                          <a href={mapsUrl} target="_blank" rel="noopener noreferrer" onClick={e => { e.stopPropagation(); }} style={{ textDecoration: "none", flexShrink: 0 }} title="Get directions">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#ea4335"/><circle cx="12" cy="9" r="2.5" fill="#fff"/></svg>
                          </a>
                        </div>
                        <div style={{ fontSize: 11, color: "#6b7280" }}>{oopExplain}</div>
                        {/* Rate range + billing class + payer badge */}
                        <div style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 5, padding: "3px 8px",
                            background: r.billingClass === "institutional" ? "#fffbeb" : "#eff6ff",
                            border: `1px solid ${r.billingClass === "institutional" ? "#fde68a" : "#bfdbfe"}`,
                            borderRadius: 6 }}>
                            <span style={{ fontSize: 10, color: r.billingClass === "institutional" ? "#d97706" : "#1d4ed8", fontWeight: 600 }}>
                              {r.billingClass === "institutional" ? "Facility fee" : "Physician fee"}
                            </span>
                            <span style={{ fontSize: 11, fontWeight: 700, color: "#111827" }}>
                              {r.minRate === r.maxRate ? fmt(r.minRate) : `${fmt(r.minRate)}–${fmt(r.maxRate)}`}
                            </span>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 4, padding: "3px 8px",
                            background: payer === "uhc" ? "#eff6ff" : payer === "aetna" ? "#fef2f2" : payer === "cigna" ? "#f0fdf4" : "#f9fafb",
                            border: `1px solid ${payer === "uhc" ? "#bfdbfe" : payer === "aetna" ? "#fecaca" : payer === "cigna" ? "#bbf7d0" : "#e5e7eb"}`,
                            borderRadius: 6 }}>
                            <span style={{ fontSize: 10, fontWeight: 700,
                              color: payer === "uhc" ? "#1d4ed8" : payer === "aetna" ? "#dc2626" : payer === "cigna" ? "#16a34a" : "#374151" }}>
                              {payerLabel}
                            </span>
                          </div>
                          {r.type && (
                            <span style={{ fontSize: 10, color: "#6b7280", padding: "3px 8px",
                              background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 6 }}>
                              {r.type}
                            </span>
                          )}
                        </div>
                      </div>

                      <div style={{ textAlign: "right", flexShrink: 0 }}>
                        <div style={{ fontSize: 24, fontWeight: 800, color: "#16a34a", lineHeight: 1 }}>
                          {r.oop === r.oopMax ? fmt(r.oop) : `${fmt(r.oop)}–${fmt(r.oopMax)}`}
                        </div>
                        <div style={{ fontSize: 10, color: "#9ca3af", marginTop: 2 }}>you pay</div>
                        <span onClick={e => { e.stopPropagation();
                          setBreakdownCard({ r, payer, payerLabel, remaining,
                            deductibleTotal, deductibleMet, coinsurance, oopMax }); }}
                          style={{ fontSize: 10, color: "#2563eb", cursor: "pointer",
                            textDecoration: "underline", marginTop: 4, display: "block",
                            textAlign: "right" }}>
                          💡 See cost breakdown
                        </span>
                        {i > 0 && (
                          <div style={{ fontSize: 11, color: "#d97706", marginTop: 4, fontWeight: 600 }}>
                            +{fmt(r.oop - (selCat === "all" ? results[0] : results.filter(x => x.provider_category === selCat)[0] || results[0]).oop)} vs cheapest
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            <div style={{ marginTop: 8, padding: "12px 16px", background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 10 }}>
              <p style={{ fontSize: 11, color: "#9ca3af", lineHeight: 1.7, margin: 0 }}>
                <strong style={{ color: "#6b7280" }}>ⓘ About these estimates:</strong> Your out-of-pocket cost is calculated using the published negotiated rate and your plan details. Actual costs may vary based on your specific plan design, network tier, and whether prior authorization is required. Always confirm with your insurer before scheduling.
              </p>
            </div>
          </>
        )}

      {breakdownCard && (() => {
        const { r, payer, payerLabel, remaining, deductibleTotal, deductibleMet, coinsurance, oopMax } = breakdownCard;
        const rate1 = r.minRate, rate2 = r.maxRate || r.minRate;
        const fmt2 = n => "$" + Math.round(n).toLocaleString();
        const fmtR = (a,b) => Math.round(a)===Math.round(b) ? fmt2(a) : fmt2(a)+"–"+fmt2(b);
        const rem = Math.max(0, deductibleTotal - deductibleMet);
        const co = coinsurance / 100;
        const s1a = Math.min(rate1, rem), s1b = Math.min(rate2, rem);
        const s2a = Math.min(rate1*co, Math.max(0, oopMax-deductibleMet));
        const s2b = Math.min(rate2*co, Math.max(0, oopMax-deductibleMet));
        return (
          <div onClick={() => setBreakdownCard(null)}
            style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.45)",
              zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}>
            <div onClick={e => e.stopPropagation()}
              style={{ background:"#fff", borderRadius:16, width:"100%", maxWidth:520,
                maxHeight:"90vh", overflowY:"auto", boxShadow:"0 20px 60px rgba(0,0,0,0.25)" }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start",
                padding:"18px 20px 12px", borderBottom:"1px solid #e5e7eb",
                position:"sticky", top:0, background:"#fff", borderRadius:"16px 16px 0 0" }}>
                <div>
                  <div style={{ fontSize:15, fontWeight:800, color:"#111827" }}>Cost Breakdown</div>
                  <div style={{ fontSize:12, color:"#6b7280", marginTop:2 }}>{r.facility} · {payerLabel}</div>
                </div>
                <button onClick={() => setBreakdownCard(null)}
                  style={{ background:"none", border:"none", fontSize:22, cursor:"pointer",
                    color:"#6b7280", padding:"0 0 0 16px" }}>×</button>
              </div>
              <div style={{ padding:"14px 20px", background:"#f8fafc", borderBottom:"1px solid #e5e7eb" }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <span style={{ fontSize:12, color:"#374151" }}>Published negotiated rate</span>
                  <span style={{ fontSize:16, fontWeight:800, color:"#111827" }}>{fmtR(rate1,rate2)}</span>
                </div>
                <div style={{ fontSize:11, color:"#6b7280", marginTop:4 }}>
                  {r.billingClass==="institutional" ? "Facility fee" : "Physician fee"} · Source: TCR-MRF
                </div>
              </div>
              <div style={{ padding:"16px 20px", display:"flex", flexDirection:"column", gap:12 }}>
                {/* ── Your plan status ── */}
                <div style={{ background:"#f8fafc", border:"1px solid #e5e7eb", borderRadius:10, padding:"12px 14px" }}>
                  <div style={{ fontWeight:700, color:"#111827", fontSize:12, marginBottom:8 }}>Your plan details</div>
                  {[["Annual deductible", fmt2(deductibleTotal)],
                    ["Already paid this year", fmt2(deductibleMet)],
                    ["Remaining deductible", fmt2(rem)],
                    ["Coinsurance after deductible", coinsurance+"%"],
                    ["Out-of-pocket maximum", fmt2(oopMax)],
                  ].map(([label,val],i) => (
                    <div key={i} style={{ display:"flex", justifyContent:"space-between",
                      fontSize:12, color:"#374151", marginBottom:4 }}>
                      <span>{label}</span><span style={{ fontWeight:600 }}>{val}</span>
                    </div>
                  ))}
                </div>
                {/* ── Your actual cost now ── */}
                {rem > 0 ? (
                  <div style={{ background:"#fffbeb", border:"1px solid #fde68a", borderRadius:10, padding:"12px 14px" }}>
                    <div style={{ fontWeight:700, color:"#92400e", fontSize:12, marginBottom:8 }}>
                      ⚠️ Your situation — Deductible not yet met
                    </div>
                    <div style={{ fontSize:12, color:"#374151", marginBottom:8, lineHeight:1.5 }}>
                      You have <strong>{fmt2(rem)}</strong> remaining on your deductible.
                      You pay 100% of the negotiated rate until it's met.
                    </div>
                    {[["Published negotiated rate", fmtR(rate1,rate2)],
                      ["You pay (100% until deductible met)", fmtR(Math.min(rate1,rem), Math.min(rate2,rem))],
                    ].map(([label,val],i) => (
                      <div key={i} style={{ display:"flex", justifyContent:"space-between",
                        fontSize:12, color:"#374151", marginBottom:4 }}>
                        <span>{label}</span><span style={{ fontWeight:600 }}>{val}</span>
                      </div>
                    ))}
                    <div style={{ borderTop:"1px solid #fde68a", marginTop:8, paddingTop:8,
                      display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                      <span style={{ fontWeight:700, color:"#92400e", fontSize:13 }}>You pay today</span>
                      <span style={{ fontWeight:800, color:"#92400e", fontSize:20 }}>{fmtR(Math.min(rate1,rem),Math.min(rate2,rem))}</span>
                    </div>
                  </div>
                ) : (
                  <div style={{ background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:10, padding:"12px 14px" }}>
                    <div style={{ fontWeight:700, color:"#16a34a", fontSize:12, marginBottom:8 }}>
                      ✅ Your situation — Deductible met
                    </div>
                    <div style={{ fontSize:12, color:"#374151", marginBottom:8, lineHeight:1.5 }}>
                      Your deductible is fully met. You pay {coinsurance}% coinsurance.
                    </div>
                    {[["Published negotiated rate", fmtR(rate1,rate2)],
                      [`Your coinsurance (${coinsurance}%)`, fmtR(Math.round(s2a),Math.round(s2b))],
                      [`Insurance pays (${100-coinsurance}%)`, fmtR(Math.round(rate1-s2a),Math.round(rate2-s2b))],
                    ].map(([label,val],i) => (
                      <div key={i} style={{ display:"flex", justifyContent:"space-between",
                        fontSize:12, color:"#374151", marginBottom:4 }}>
                        <span>{label}</span><span style={{ fontWeight:600 }}>{val}</span>
                      </div>
                    ))}
                    <div style={{ borderTop:"1px solid #bbf7d0", marginTop:8, paddingTop:8,
                      display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                      <span style={{ fontWeight:700, color:"#16a34a", fontSize:13 }}>You pay today</span>
                      <span style={{ fontWeight:800, color:"#16a34a", fontSize:20 }}>{fmtR(Math.round(s2a),Math.round(s2b))}</span>
                    </div>
                  </div>
                )}
                {/* ── What-if: after deductible met ── */}
                {rem > 0 && (
                  <div style={{ background:"#eff6ff", border:"1px solid #bfdbfe", borderRadius:10, padding:"12px 14px" }}>
                    <div style={{ fontWeight:700, color:"#1d4ed8", fontSize:12, marginBottom:8 }}>
                      💡 What if your deductible were met?
                    </div>
                    {[["Published negotiated rate", fmtR(rate1,rate2)],
                      [`Your coinsurance (${coinsurance}%)`, fmtR(Math.round(s2a),Math.round(s2b))],
                      [`Insurance pays (${100-coinsurance}%)`, fmtR(Math.round(rate1-s2a),Math.round(rate2-s2b))],
                    ].map(([label,val],i) => (
                      <div key={i} style={{ display:"flex", justifyContent:"space-between",
                        fontSize:12, color:"#374151", marginBottom:4 }}>
                        <span>{label}</span><span style={{ fontWeight:600 }}>{val}</span>
                      </div>
                    ))}
                    <div style={{ borderTop:"1px solid #bfdbfe", marginTop:8, paddingTop:8,
                      display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                      <span style={{ fontWeight:700, color:"#1d4ed8", fontSize:13 }}>You'd pay</span>
                      <span style={{ fontWeight:800, color:"#1d4ed8", fontSize:20 }}>{fmtR(Math.round(s2a),Math.round(s2b))}</span>
                    </div>
                  </div>
                )}
                {/* ── OOP max reached ── */}
                <div style={{ background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:10, padding:"12px 14px" }}>
                  <div style={{ fontWeight:700, color:"#16a34a", fontSize:12, marginBottom:8 }}>
                    🌟 After reaching OOP max ({fmt2(oopMax)})
                  </div>
                  <div style={{ fontSize:12, color:"#374151", marginBottom:4, lineHeight:1.5 }}>
                    Once you hit your out-of-pocket maximum, insurance covers 100% of in-network costs for the rest of the year.
                  </div>
                  <div style={{ borderTop:"1px solid #bbf7d0", marginTop:8, paddingTop:8,
                    display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                    <span style={{ fontWeight:700, color:"#16a34a", fontSize:13 }}>You pay</span>
                    <span style={{ fontWeight:800, color:"#16a34a", fontSize:20 }}>$0</span>
                  </div>
                </div>
                <div style={{ fontSize:10, color:"#9ca3af", lineHeight:1.6 }}>
                  ⓘ Based on published negotiated rates from federal TCR-MRF data. Actual costs may vary. Always confirm with your insurer before scheduling.
                </div>
              </div>
            </div>
          </div>
        );
      })()}

        {!results.length && !running && !error && (
          <div style={{ textAlign: "center", padding: "40px 0 32px" }}>
            <div style={{ fontSize: 42, marginBottom: 14 }}>💰</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#111827", marginBottom: 8 }}>Know exactly what you'll pay before you go</div>
            <div style={{ fontSize: 14, color: "#9ca3af", lineHeight: 1.6, maxWidth: 420, margin: "0 auto 20px" }}>
              Enter your plan details and we'll calculate your real out-of-pocket cost using published MRF negotiated rates — not estimates.
            </div>
            {/* HDHP callout */}
            <div style={{ display: "inline-flex", alignItems: "flex-start", gap: 12, background: "#eff6ff", border: "1.5px solid #bfdbfe", borderRadius: 12, padding: "14px 18px", maxWidth: 460, textAlign: "left" }}>
              <span style={{ fontSize: 22, flexShrink: 0 }}>🏦</span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#1d4ed8", marginBottom: 4 }}>
                  On an HDHP or HSA plan?
                </div>
                <div style={{ fontSize: 12, color: "#3b82f6", lineHeight: 1.6 }}>
                  With a high-deductible plan, you pay the full negotiated rate until your deductible is met — often thousands of dollars a year.
                  Cost Check shows your exact out-of-pocket for every facility near you, so you can choose the cheapest option before you schedule.
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
